import axios from 'axios'
import { useEffect, useState } from 'react'
import { clearMask, formatCep } from 'utils/formatters'
import { TrailField, TrailHeading } from './screens'

export type TBillingAddress = {
  zip_code: string
  street: string
  address_number: string
  complement: string
  district: string
  city: string
  state: string
}

const EMPTY: TBillingAddress = {
  zip_code: '',
  street: '',
  address_number: '',
  complement: '',
  district: '',
  city: '',
  state: ''
}

export const isBillingAddressValid = (address: TBillingAddress) =>
  clearMask(address.zip_code).length === 8 &&
  Boolean(address.street.trim()) &&
  Boolean(address.address_number.trim()) &&
  Boolean(address.city.trim()) &&
  /^[A-Za-z]{2}$/.test(address.state.trim())

// Endereço de cobrança do cartão. O programa cobra antes de existir endereço de entrega, e o gateway exige um
// endereço de cobrança válido para cartão.
export const BillingAddressForm = ({ onChange }: { onChange: (address: TBillingAddress | null) => void }) => {
  const [address, setAddress] = useState<TBillingAddress>(EMPTY)
  const [lookupError, setLookupError] = useState('')
  const [lookingUp, setLookingUp] = useState(false)

  const update = (field: keyof TBillingAddress, value: string) =>
    setAddress((current) => ({ ...current, [field]: value }))

  useEffect(() => {
    onChange(isBillingAddressValid(address) ? address : null)
  }, [address])

  useEffect(() => {
    const digits = clearMask(address.zip_code)
    if (digits.length !== 8) {
      setLookupError('')
      return undefined
    }

    let cancelled = false
    setLookingUp(true)
    axios
      .get(`https://viacep.com.br/ws/${digits}/json/`)
      .then(({ data }) => {
        if (cancelled) return
        if (data?.erro) {
          setLookupError('CEP não encontrado. Preencha o endereço manualmente.')
          return
        }
        setLookupError('')
        setAddress((current) => ({
          ...current,
          street: data?.logradouro || current.street,
          district: data?.bairro || current.district,
          city: data?.localidade || current.city,
          state: data?.uf || current.state
        }))
      })
      .catch(() => {
        if (!cancelled) setLookupError('Não foi possível buscar o CEP. Preencha o endereço manualmente.')
      })
      .finally(() => {
        if (!cancelled) setLookingUp(false)
      })

    return () => {
      cancelled = true
    }
  }, [address.zip_code])

  return (
    <div className="flex flex-col gap-4">
      <TrailHeading title="Endereço de cobrança" sub="Endereço da fatura do cartão." />
      <TrailField
        label="CEP"
        value={address.zip_code}
        onChange={(value) => update('zip_code', formatCep(value))}
        inputMode="numeric"
        placeholder="00000-000"
        hint={lookingUp ? 'Buscando endereço…' : lookupError || undefined}
      />
      <TrailField label="Rua" value={address.street} onChange={(value) => update('street', value)} />
      <div className="grid grid-cols-2 gap-3">
        <TrailField
          label="Número"
          value={address.address_number}
          onChange={(value) => update('address_number', value)}
          placeholder="Número ou S/N"
        />
        <TrailField
          label="Complemento"
          value={address.complement}
          onChange={(value) => update('complement', value)}
          placeholder="Opcional"
        />
      </div>
      <TrailField label="Bairro" value={address.district} onChange={(value) => update('district', value)} />
      <div className="grid grid-cols-[1fr_88px] gap-3">
        <TrailField label="Cidade" value={address.city} onChange={(value) => update('city', value)} />
        <TrailField
          label="UF"
          value={address.state}
          onChange={(value) =>
            update(
              'state',
              value
                .replace(/[^A-Za-z]/g, '')
                .slice(0, 2)
                .toUpperCase()
            )
          }
          placeholder="SP"
        />
      </div>
    </div>
  )
}
